import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import * as THREE from 'three';
import {
  SPACE_CLASS_LABEL,
  VIDEO_ENVIRONMENTS,
  type VideoEnvironmentDefinition,
  type VideoEnvironmentId,
} from '../data/videoBackgroundEnvironments';
import {
  NPC_CATALOG,
  type CatalogDialogueChoice,
  type CatalogNpc,
} from '../data/videoNpcCatalog';
import {
  floorExtents,
  imagePointToGround,
  pointInPolygonXZ,
  projectedActorBounds,
  projectedHeightPixels,
  resolveWalkableFloor,
  screenSpaceSeparationPx,
  validateBackgroundLayout,
  type ProjectedActorBounds,
  type WorldPoint,
} from './videoBackgroundLayout';
import {
  BLOCKING_SCENARIOS,
  PORTRAIT_ACTOR_HEIGHT,
  PORTRAIT_MIN_SCREEN_GAP_PX,
  blockingPointToWorld,
  resolveBlockingScenario,
  type BlockingAction,
  type BlockingPlacement,
  type BlockingScenarioId,
} from './videoBlocking';
import {
  cameraRelativeYaw,
  resolveBillboardGeometry,
  selectBillboardView,
  type BillboardAssets,
} from './videoNpcBillboard';
import {
  applyLookDelta as resolveLookDelta,
  conversationCameraPosition,
  movementBoundsAroundPoints,
  resolveBoundedMovementXZ,
} from './videoCameraControls';
import './VideoBillboardGame.css';
import { useViewportSize } from '../hooks/useViewportWidth';

type NpcId = CatalogNpc['id'];
type EnvironmentId = VideoEnvironmentId;
type ActorRenderMode = 'blocking' | 'frames' | 'sprite';
type ActiveBlockingScenario = BlockingScenarioId | 'baseline';
const SPRITE_NPC_IDS = new Set<NpcId>(NPC_CATALOG.npcs.map((npc) => npc.id));
const NPC_BILLBOARD_ASSETS = Object.fromEntries(
  NPC_CATALOG.npcs.map((npc) => [npc.id, npc.assets]),
) as Record<NpcId, BillboardAssets>;
const NPC_DIALOGUE_REACTIONS = Object.fromEntries(
  NPC_CATALOG.npcs.map((npc) => [npc.id, npc.reactionByChoice]),
) as Record<NpcId, Record<string, 'positive' | 'negative'>>;

interface VideoBillboardGameProps {
  onBack: () => void;
}

type DialogueChoice = CatalogDialogueChoice;
type NpcDefinition = CatalogNpc;

interface ResolvedNpcDefinition extends NpcDefinition {
  feet: [number, number];
  facingY: number;
  scale: number;
  blockingRole: string;
  action: BlockingAction;
  movementTarget: WorldPoint | null;
  pathWorld: WorldPoint[] | null;
}

interface HudState {
  closestNpcId: NpcId | null;
  distance: number;
  canInteract: boolean;
}

interface ReputationState {
  respect: number;
  trouble: number;
  rumor: number;
}

declare global {
  interface Window {
    __videoBillboardState?: {
      ready: boolean;
      videoReady: boolean;
      characterVisible: boolean;
      characterPosition: [number, number, number];
      cameraPosition: [number, number, number];
      distanceToCharacter: number;
      interactionAvailable: boolean;
      pointerLocked: boolean;
      mouseDragging: boolean;
      mouseLookReady: boolean;
      cameraRotation: [number, number];
      viewport: { width: number; height: number };
      mobileFraming: boolean;
      currentAction: string;
      chromaKey: boolean;
      hallwayReady: boolean;
      npcCount: number;
      closestNpcId: NpcId | null;
      activeNpcId: NpcId | null;
      dialogueOpen: boolean;
      selectedChoiceLabel: string;
      respect: number;
      trouble: number;
      rumor: number;
      environmentId: EnvironmentId;
      environmentName: string;
      backgroundReady: boolean;
      backgroundImageSrc: string;
      backgroundMode: VideoEnvironmentDefinition['backdrop'];
      floorDepthMeters: number;
      movementBounds: { minX: number; maxX: number; minZ: number; maxZ: number };
      npcPlacements: Array<{
        id: NpcId;
        feet: [number, number];
        worldPosition: [number, number, number];
        facingY: number;
        characterHeightMeters: number;
        resolvedScale: number;
        projectedHeightPx: number;
        projectedBounds: ProjectedActorBounds;
        screenVisible: boolean;
        insideWalkableFloor: boolean;
        role: string;
        action: BlockingAction;
        movementTarget: WorldPoint | null;
        path: WorldPoint[] | null;
      }>;
      spaceClass: 'small-room' | 'hallway' | 'large-campus';
      walkableFloorValid: boolean;
      renderedNpcPositions: Partial<Record<NpcId, [number, number, number]>>;
      actorRenderMode: ActorRenderMode;
      spriteNpcCount: number;
      zoeSpriteView: string | null;
      zoeSpriteYawDegrees: number;
      blockingScenario: ActiveBlockingScenario;
      blockingScenarioLabel: string;
      minScreenSeparationPx: number;
      screenFrameValid: boolean;
    };
  }
}

const PLAYER_HEIGHT = 1.66;
const WALK_SPEED = 4.55;
const INTERACTION_RADIUS = 3.05;
const SOURCE_PLANE_HEIGHT = 2.04;
const SOURCE_PLANE_WIDTH = 1.11;
const MOUSE_LOOK_SENSITIVITY = 0.0032;
const MIN_LOOK_PITCH = -Math.PI * 0.46;
const MAX_LOOK_PITCH = Math.PI * 0.46;
const PROJECTED_PLATE_MAX_YAW = Math.PI * 0.42;
const INITIAL_REPUTATION: ReputationState = { respect: 3, trouble: 2, rumor: 1 };
const ENVIRONMENTS = VIDEO_ENVIRONMENTS;

const NPCS: NpcDefinition[] = NPC_CATALOG.npcs;

const NPC_BY_ID = new Map<NpcId, NpcDefinition>(NPCS.map((npc) => [npc.id, npc]));

function resolveNpcPose(
  npc: NpcDefinition,
  environment: VideoEnvironmentDefinition,
  analyzedPlacement: ReturnType<typeof validateBackgroundLayout>['placements'][number] | undefined,
  scenarioPlacement?: BlockingPlacement,
  viewport?: { width: number; height: number },
) {
  const useCatalogDefaults = environment.id === 'clubroom';
  const feet = useCatalogDefaults
    ? npc.defaults.feet
    : scenarioPlacement?.feet ?? analyzedPlacement?.feet ?? npc.defaults.feet;
  const worldPosition = useCatalogDefaults
    ? imagePointToGround(feet, environment.camera) ?? npc.defaults.position
    : scenarioPlacement?.worldPosition ?? analyzedPlacement?.worldPosition ?? npc.defaults.position;

  return {
    feet,
    facingY: useCatalogDefaults ? npc.defaults.facingY : scenarioPlacement?.facingY ?? npc.defaults.facingY,
    note: useCatalogDefaults ? npc.defaults.blockingRole : scenarioPlacement?.role ?? npc.defaults.blockingRole,
    action: useCatalogDefaults ? npc.defaults.action : scenarioPlacement?.action ?? npc.defaults.action,
    movementTarget: scenarioPlacement?.movementTarget
      ? blockingPointToWorld(environment, scenarioPlacement.movementTarget, viewport)
      : null,
    pathWorld: scenarioPlacement?.path
      ?.map((point) => blockingPointToWorld(environment, point, viewport))
      .filter((point): point is WorldPoint => point !== null) ?? null,
    worldPosition,
  };
}

const vertexShader = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform sampler2D map;
  varying vec2 vUv;

  void main() {
    vec4 texel = texture2D(map, vUv);
    float greenDominance = texel.g - max(texel.r, texel.b);
    float alpha = 1.0 - smoothstep(0.035, 0.16, greenDominance);

    vec3 color = texel.rgb;
    color.g = min(color.g, max(color.r, color.b) * 1.08);
    color *= 0.88 + (vUv.y * 0.12);

    if (alpha < 0.02) discard;
    gl_FragColor = vec4(color, alpha);
  }
`;

const environmentVertexShader = /* glsl */ `
  uniform mat4 projectorMatrix;
  varying vec4 vProjectedPosition;

  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vProjectedPosition = projectorMatrix * worldPosition;
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

const environmentFragmentShader = /* glsl */ `
  uniform sampler2D map;
  uniform vec3 fallbackColor;
  uniform bool transparentOutside;
  varying vec4 vProjectedPosition;

  void main() {
    vec2 projectedUv = (vProjectedPosition.xy / vProjectedPosition.w) * 0.5 + 0.5;
    bool outsidePlate = vProjectedPosition.w <= 0.0
      || projectedUv.x < 0.0 || projectedUv.x > 1.0
      || projectedUv.y < 0.0 || projectedUv.y > 1.0;

    if (outsidePlate && transparentOutside) {
      gl_FragColor = vec4(0.0);
      return;
    }

    // Portrait view can see a little beyond the canonical 16:9 projection.
    // Clamp to the captured plate edge instead of exposing a black fallback.
    vec2 safeUv = clamp(projectedUv, vec2(0.0), vec2(1.0));
    vec3 color = texture2D(map, safeUv).rgb;
    gl_FragColor = vec4(color, 1.0);
    #include <colorspace_fragment>
  }
`;

function setState(patch: Partial<NonNullable<Window['__videoBillboardState']>>) {
  if (!window.__videoBillboardState) return;
  Object.assign(window.__videoBillboardState, patch);
}

function videoLabel(npc: NpcDefinition, isActive: boolean) {
  return isActive
    ? npc.clips?.react ?? { loop: false, src: '' }
    : npc.clips?.idle ?? { loop: true, src: '' };
}

function VideoNpc({
  active,
  npc,
  onEnded,
  onPositionResolved,
  onVideoReady,
  replayKey,
}: {
  active: boolean;
  npc: ResolvedNpcDefinition;
  onEnded: (id: NpcId) => void;
  onPositionResolved: (id: NpcId, position: WorldPoint) => void;
  onVideoReady: (id: NpcId) => void;
  replayKey: number;
}) {
  const group = useRef<THREE.Group>(null);
  const { camera } = useThree();
  const readyRef = useRef(false);
  const clip = videoLabel(npc, active);

  const video = useMemo(() => {
    const element = document.createElement('video');
    element.crossOrigin = 'anonymous';
    element.muted = true;
    element.playsInline = true;
    element.preload = 'auto';
    return element;
  }, []);

  const texture = useMemo(() => {
    const next = new THREE.VideoTexture(video);
    next.colorSpace = THREE.SRGBColorSpace;
    next.minFilter = THREE.LinearFilter;
    next.magFilter = THREE.LinearFilter;
    next.generateMipmaps = false;
    return next;
  }, [video]);

  const material = useMemo(() => new THREE.ShaderMaterial({
    uniforms: { map: { value: texture } },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false,
  }), [texture]);

  useEffect(() => {
    const markReady = () => {
      if (readyRef.current) return;
      readyRef.current = true;
      onVideoReady(npc.id);
    };
    const handleError = () => setState({ videoReady: false });
    video.addEventListener('canplay', markReady);
    video.addEventListener('error', handleError);
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) markReady();

    return () => {
      video.removeEventListener('canplay', markReady);
      video.removeEventListener('error', handleError);
      video.pause();
      video.removeAttribute('src');
      video.load();
      texture.dispose();
      material.dispose();
    };
  }, [material, npc.id, onVideoReady, texture, video]);

  useEffect(() => {
    const playbackSrc = clip.loop ? clip.src : `${clip.src}?reaction=${replayKey}`;
    const desiredSrc = new URL(playbackSrc, window.location.href).href;
    video.pause();
    if (clip.loop) video.setAttribute('loop', '');
    else video.removeAttribute('loop');
    if (video.currentSrc !== desiredSrc) {
      video.setAttribute('src', playbackSrc);
      video.load();
    }
    void video.play().catch(() => undefined);
  }, [clip.loop, clip.src, replayKey, video]);

  useEffect(() => {
    const handleEnded = () => onEnded(npc.id);
    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [npc.id, onEnded, video]);

  useEffect(() => {
    if (!group.current) return;
    onPositionResolved(npc.id, group.current.position.toArray() as WorldPoint);
  }, [npc.id, npc.position, onPositionResolved]);

  useFrame(() => {
    if (!group.current) return;
    const dx = camera.position.x - group.current.position.x;
    const dz = camera.position.z - group.current.position.z;
    group.current.rotation.y = Math.atan2(dx, dz);
  });

  return (
    <group ref={group} position={npc.position}>
      <mesh position={[0, (SOURCE_PLANE_HEIGHT * npc.scale) / 2, 0]} material={material} frustumCulled={false}>
        <planeGeometry args={[SOURCE_PLANE_WIDTH * npc.scale, SOURCE_PLANE_HEIGHT * npc.scale]} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.018, 0]}>
        <circleGeometry args={[0.34 * npc.scale, 48]} />
        <meshBasicMaterial color="#0b0806" transparent opacity={0.46} depthWrite={false} />
      </mesh>
      <pointLight position={[0, 1.05, 0.2]} color={npc.accent} intensity={0.55} distance={1.8} />
    </group>
  );
}

function SpriteNpc({
  npc,
  onPositionResolved,
  onVideoReady,
  onSpriteViewChange,
  reportView,
  forceFront,
  dialogueReaction,
}: {
  npc: ResolvedNpcDefinition;
  onPositionResolved: (id: NpcId, position: WorldPoint) => void;
  onVideoReady: (id: NpcId) => void;
  onSpriteViewChange: (view: string, relativeYawDegrees: number) => void;
  reportView: boolean;
  forceFront: boolean;
  dialogueReaction: 'positive' | 'negative' | null;
}) {
  const group = useRef<THREE.Group>(null);
  const { camera } = useThree();
  const assets = NPC_BILLBOARD_ASSETS[npc.id];
  const spriteSources = useMemo(() => [
    ...assets.views.map((view) => view.src),
    assets.reactions.positive.src,
    assets.reactions.negative.src,
  ], [assets]);
  const textures = useLoader(THREE.TextureLoader, spriteSources);
  const previousViewId = useRef<string | null>(null);
  const [selectedViewId, setSelectedViewId] = useState('front');
  const preparedTextures = useMemo(() => {
    textures.forEach((texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.needsUpdate = true;
    });
    return textures;
  }, [textures]);
  const selectedSource = dialogueReaction
    ? assets.reactions[dialogueReaction]
    : assets.views.find((view) => view.id === selectedViewId) ?? assets.views[0];
  const selectedTexture = preparedTextures[spriteSources.indexOf(selectedSource.src)];
  const frontGeometry = resolveBillboardGeometry(npc.heightMeters, assets.views[0], SOURCE_PLANE_HEIGHT);
  const selectedGeometry = resolveBillboardGeometry(npc.heightMeters, selectedSource, SOURCE_PLANE_HEIGHT);
  const placementScaleCorrection = npc.scale / frontGeometry.scale;
  const planeHeight = selectedGeometry.planeHeight * placementScaleCorrection;
  const planeWidth = selectedGeometry.planeWidth * placementScaleCorrection;
  const meshCenterY = selectedGeometry.meshCenterY * placementScaleCorrection;

  useEffect(() => {
    onVideoReady(npc.id);
  }, [npc.id, onVideoReady]);

  useEffect(() => {
    if (!group.current) return;
    onPositionResolved(npc.id, group.current.position.toArray() as WorldPoint);
  }, [npc.id, npc.position, onPositionResolved]);

  useFrame(() => {
    if (!group.current) return;
    const cameraPosition: [number, number, number] = [camera.position.x, camera.position.y, camera.position.z];
    const dx = camera.position.x - group.current.position.x;
    const dz = camera.position.z - group.current.position.z;
    group.current.rotation.y = Math.atan2(dx, dz);
    const relativeYawRadians = cameraRelativeYaw(cameraPosition, npc.position, npc.facingY);
    const selection = forceFront
      ? { view: assets.views[0], relativeYawRadians: 0 }
      : selectBillboardView(relativeYawRadians, assets.views, previousViewId.current);
    if (selection.view.id === previousViewId.current) return;
    previousViewId.current = selection.view.id;
    setSelectedViewId(selection.view.id);
    if (!reportView) return;
    const relativeYawDegrees = THREE.MathUtils.radToDeg(selection.relativeYawRadians);
    setState({ zoeSpriteView: selection.view.id, zoeSpriteYawDegrees: relativeYawDegrees });
    onSpriteViewChange(selection.view.id, relativeYawDegrees);
  });

  return (
    <group ref={group} position={npc.position}>
      <mesh position={[0, meshCenterY, 0]} frustumCulled={false}>
        <planeGeometry args={[planeWidth, planeHeight]} />
        <meshBasicMaterial
          map={selectedTexture}
          transparent
          alphaTest={0.02}
          depthTest
          depthWrite={false}
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.018, 0]}>
        <circleGeometry args={[0.34 * npc.scale, 48]} />
        <meshBasicMaterial color="#0b0806" transparent opacity={0.46} depthWrite={false} />
      </mesh>
    </group>
  );
}

function BlockNpc({
  npc,
  onPositionResolved,
  onVideoReady,
}: {
  npc: ResolvedNpcDefinition;
  onPositionResolved: (id: NpcId, position: WorldPoint) => void;
  onVideoReady: (id: NpcId) => void;
}) {
  const group = useRef<THREE.Group>(null);
  const { size } = useThree();
  const portraitScale = size.height > size.width ? PORTRAIT_ACTOR_HEIGHT / npc.heightMeters : 1;
  const torsoHeight = Math.max(npc.heightMeters * 0.58, 0.86);
  const legHeight = Math.max(npc.heightMeters * 0.34, 0.48);
  const headRadius = Math.max(npc.heightMeters * 0.09, 0.13);

  useEffect(() => {
    if (!group.current) return;
    onPositionResolved(npc.id, group.current.position.toArray() as WorldPoint);
    onVideoReady(npc.id);
  }, [npc.id, npc.position, onPositionResolved, onVideoReady]);

  return (
    <group ref={group} position={npc.position} rotation={[0, npc.facingY, 0]} scale={portraitScale}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.018, 0]}>
        <circleGeometry args={[0.34, 48]} />
        <meshBasicMaterial color="#0b0806" transparent opacity={0.46} depthWrite={false} />
      </mesh>
      <mesh position={[0, legHeight / 2, 0]}>
        <boxGeometry args={[0.42, legHeight, 0.24]} />
        <meshStandardMaterial color="#22242a" roughness={0.72} />
      </mesh>
      <mesh position={[0, legHeight + (torsoHeight / 2), 0]}>
        <boxGeometry args={[0.58, torsoHeight, 0.32]} />
        <meshStandardMaterial color={npc.accent} roughness={0.68} />
      </mesh>
      <mesh position={[0, legHeight + torsoHeight + headRadius * 1.06, 0]}>
        <sphereGeometry args={[headRadius, 24, 16]} />
        <meshStandardMaterial color="#f0c394" roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.02, -0.33]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.11, 0.34, 3]} />
        <meshBasicMaterial color={npc.action === 'crossing' ? '#f0bd53' : '#fff5d6'} transparent opacity={0.9} />
      </mesh>
      <mesh position={[0, 0.045, -0.48]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.42, 0.08]} />
        <meshBasicMaterial color={npc.accent} transparent opacity={0.86} depthWrite={false} />
      </mesh>
      <pointLight position={[0, 1.15, 0.15]} color={npc.accent} intensity={0.32} distance={1.7} />
    </group>
  );
}

function FirstPersonController({ activeConversationNpc, controlsEnabled, environment, npcs, onHudChange, resetKey }: {
  activeConversationNpc: ResolvedNpcDefinition | null;
  controlsEnabled: boolean;
  environment: VideoEnvironmentDefinition;
  npcs: NpcDefinition[];
  onHudChange: (state: HudState) => void;
  resetKey: string;
}) {
  const { camera, gl } = useThree();
  const keys = useRef(new Set<string>());
  const lastHudUpdate = useRef(0);
  const elapsed = useRef(0);
  const forward = useMemo(() => new THREE.Vector3(), []);
  const right = useMemo(() => new THREE.Vector3(), []);
  const move = useMemo(() => new THREE.Vector3(), []);
  const up = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const cameraEuler = useRef(new THREE.Euler(0, 0, 0, 'YXZ'));
  const baseYaw = useRef(0);
  const dragging = useRef(false);
  const activePointerId = useRef<number | null>(null);
  const pointerPosition = useRef<[number, number]>([0, 0]);
  const movementBounds = useMemo(() => movementBoundsAroundPoints(
    [environment.camera.position, ...npcs.map((npc) => npc.position)],
    environment.movementMargin,
  ), [environment.camera.position, environment.movementMargin, npcs]);
  const conversationLookTarget = useMemo(() => new THREE.Vector3(), []);
  const conversationQuaternion = useMemo(() => new THREE.Quaternion(), []);
  const conversationMatrix = useMemo(() => new THREE.Matrix4(), []);

  useEffect(() => {
    camera.position.set(...environment.camera.position);
    camera.lookAt(...environment.camera.target);
    cameraEuler.current.setFromQuaternion(camera.quaternion, 'YXZ');
    baseYaw.current = cameraEuler.current.y;
    keys.current.clear();
    setState({
      cameraRotation: [cameraEuler.current.x, cameraEuler.current.y],
      mouseDragging: false,
      mouseLookReady: true,
      pointerLocked: false,
    });
  }, [camera, environment, resetKey]);

  useEffect(() => {
    const canvas = gl.domElement;
    const keyState = keys.current;
    const applyLookDelta = (deltaX: number, deltaY: number) => {
      const nextRotation = resolveLookDelta(
        { pitch: cameraEuler.current.x, yaw: cameraEuler.current.y },
        deltaX,
        deltaY,
        MOUSE_LOOK_SENSITIVITY,
        MIN_LOOK_PITCH,
        MAX_LOOK_PITCH,
        environment.backdrop === 'generated-cubemap' ? null : baseYaw.current,
        PROJECTED_PLATE_MAX_YAW,
      );
      cameraEuler.current.x = nextRotation.pitch;
      cameraEuler.current.y = nextRotation.yaw;
      cameraEuler.current.z = 0;
      camera.quaternion.setFromEuler(cameraEuler.current);
      setState({ cameraRotation: [cameraEuler.current.x, cameraEuler.current.y] });
    };

    const stopDragging = (event?: PointerEvent) => {
      if (event && activePointerId.current !== event.pointerId) return;
      const pointerId = activePointerId.current;
      dragging.current = false;
      activePointerId.current = null;
      if (pointerId !== null && canvas.hasPointerCapture(pointerId)) canvas.releasePointerCapture(pointerId);
      if (document.pointerLockElement === canvas) void document.exitPointerLock();
      setState({ mouseDragging: false, pointerLocked: false });
    };
    const onPointerLockChange = () => setState({ pointerLocked: document.pointerLockElement === canvas });
    const onKeyDown = (event: KeyboardEvent) => {
      if (controlsEnabled) keyState.add(event.code);
    };
    const onKeyUp = (event: KeyboardEvent) => keyState.delete(event.code);
    const onPointerDown = (event: PointerEvent) => {
      if (!controlsEnabled || event.button !== 0) return;
      if (!event.isPrimary || activePointerId.current !== null) {
        if (event.pointerType === 'touch') stopDragging();
        event.preventDefault();
        return;
      }
      dragging.current = true;
      activePointerId.current = event.pointerId;
      pointerPosition.current = [event.clientX, event.clientY];
      canvas.setPointerCapture(event.pointerId);
      if (event.pointerType === 'mouse') canvas.requestPointerLock?.();
      setState({ mouseDragging: true });
      event.preventDefault();
    };
    const onPointerMove = (event: PointerEvent) => {
      if (!controlsEnabled || !dragging.current || activePointerId.current !== event.pointerId) return;
      const [lastX, lastY] = pointerPosition.current;
      const pointerLocked = document.pointerLockElement === canvas;
      const deltaX = pointerLocked ? event.movementX : event.clientX - lastX;
      const deltaY = pointerLocked ? event.movementY : event.clientY - lastY;
      pointerPosition.current = [event.clientX, event.clientY];
      applyLookDelta(deltaX, deltaY);
      event.preventDefault();
    };
    const onBlur = () => {
      keyState.clear();
      stopDragging();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') stopDragging();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', stopDragging);
    canvas.addEventListener('pointercancel', stopDragging);
    canvas.addEventListener('lostpointercapture', stopDragging);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);
    if (!controlsEnabled) {
      keyState.clear();
      stopDragging();
    }
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', stopDragging);
      canvas.removeEventListener('pointercancel', stopDragging);
      canvas.removeEventListener('lostpointercapture', stopDragging);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointercancel', stopDragging);
      if (document.pointerLockElement === canvas) void document.exitPointerLock();
      keyState.clear();
      dragging.current = false;
      activePointerId.current = null;
    };
  }, [camera, controlsEnabled, environment.backdrop, gl]);

  useFrame((_, delta) => {
    const step = Math.min(delta, 0.05);
    if (activeConversationNpc) {
      const desiredDestination = conversationCameraPosition(activeConversationNpc.position, activeConversationNpc.facingY, 1.7);
      const destination = resolveBoundedMovementXZ(desiredDestination, [0, 0, 0], movementBounds);
      camera.position.set(
        THREE.MathUtils.damp(camera.position.x, destination[0], 7.5, step),
        THREE.MathUtils.damp(camera.position.y, PLAYER_HEIGHT, 7.5, step),
        THREE.MathUtils.damp(camera.position.z, destination[2], 7.5, step),
      );
      conversationLookTarget.set(
        activeConversationNpc.position[0],
        Math.min(activeConversationNpc.heightMeters * 0.82, 1.55),
        activeConversationNpc.position[2],
      );
      conversationMatrix.lookAt(camera.position, conversationLookTarget, up);
      conversationQuaternion.setFromRotationMatrix(conversationMatrix);
      camera.quaternion.slerp(conversationQuaternion, 1 - Math.exp(-9 * step));
      cameraEuler.current.setFromQuaternion(camera.quaternion, 'YXZ');
      setState({
        cameraPosition: [camera.position.x, camera.position.y, camera.position.z],
        cameraRotation: [cameraEuler.current.x, cameraEuler.current.y],
      });
      return;
    }
    move.set(0, 0, 0);
    camera.getWorldDirection(forward);
    forward.setY(0);
    forward.normalize();
    right.crossVectors(forward, up).normalize();

    if (keys.current.has('KeyW') || keys.current.has('ArrowUp')) move.add(forward);
    if (keys.current.has('KeyS') || keys.current.has('ArrowDown')) move.sub(forward);
    if (keys.current.has('KeyD') || keys.current.has('ArrowRight')) move.add(right);
    if (keys.current.has('KeyA') || keys.current.has('ArrowLeft')) move.sub(right);

    const isMoving = move.lengthSq() > 0;
    if (isMoving) {
      move.normalize().multiplyScalar(WALK_SPEED * step);
      const current: WorldPoint = [camera.position.x, 0, camera.position.z];
      const resolved = resolveBoundedMovementXZ(current, [move.x, 0, move.z], movementBounds);
      camera.position.setX(resolved[0]);
      camera.position.setZ(resolved[2]);
      elapsed.current += step;
    }
    camera.position.setY(PLAYER_HEIGHT + (isMoving ? Math.sin(elapsed.current * 10.5) * 0.022 : 0));

    let closestNpcId: NpcId | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (const npc of npcs) {
      const distance = Math.hypot(camera.position.x - npc.position[0], camera.position.z - npc.position[2]);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestNpcId = npc.id;
      }
    }

    const canInteract = closestDistance < INTERACTION_RADIUS;
    setState({
      cameraPosition: [camera.position.x, camera.position.y, camera.position.z],
      distanceToCharacter: closestDistance,
      interactionAvailable: canInteract,
      characterVisible: true,
      closestNpcId: canInteract ? closestNpcId : null,
    });

    lastHudUpdate.current += step;
    if (lastHudUpdate.current > 0.08) {
      lastHudUpdate.current = 0;
      onHudChange({ closestNpcId: canInteract ? closestNpcId : null, distance: closestDistance, canInteract });
    }
  });

  return null;
}

function CubemapBackdrop({
  environment,
  onReady,
}: {
  environment: VideoEnvironmentDefinition;
  onReady: (ready: boolean) => void;
}) {
  const [cubeTexture, setCubeTexture] = useState<THREE.CubeTexture | null>(null);
  const shell = useMemo(() => {
    const bounds = floorExtents(resolveWalkableFloor(environment));
    const [cameraX, cameraY, cameraZ] = environment.camera.position;
    const margin = 1.5;
    const halfWidth = Math.max(Math.abs(bounds.minX - cameraX), Math.abs(bounds.maxX - cameraX)) + margin;
    const halfDepth = Math.max(Math.abs(bounds.minZ - cameraZ), Math.abs(bounds.maxZ - cameraZ)) + margin;
    const halfHeight = Math.max(cameraY, environment.shellHeight - cameraY) + margin;
    return {
      position: environment.camera.position,
      size: [halfWidth * 2, halfHeight * 2, halfDepth * 2] as [number, number, number],
    };
  }, [environment]);

  useEffect(() => {
    const faces = environment.cubemapFaces;
    if (!faces || environment.backdrop !== 'generated-cubemap') {
      onReady(false);
      return undefined;
    }
    let disposed = false;
    const loader = new THREE.CubeTextureLoader();
    const cubeTexture = loader.load(
      [...faces],
      (loaded) => {
        if (disposed) {
          loaded.dispose();
          return;
        }
        loaded.colorSpace = THREE.SRGBColorSpace;
        loaded.needsUpdate = true;
        setCubeTexture(loaded);
        onReady(true);
        setState({ backgroundReady: true });
      },
      undefined,
      () => {
        if (disposed) return;
        onReady(false);
        setState({ backgroundReady: false });
      },
    );
    return () => {
      disposed = true;
      cubeTexture.dispose();
    };
  }, [environment.backdrop, environment.cubemapFaces, onReady]);

  const material = useMemo(() => {
    if (!cubeTexture) return null;
    const shader = THREE.ShaderLib.cube;
    const uniforms = THREE.UniformsUtils.clone(shader.uniforms);
    uniforms.tCube.value = cubeTexture;
    uniforms.tFlip.value = -1;
    uniforms.opacity.value = 1;
    return new THREE.ShaderMaterial({
      name: 'WorldAnchoredCubemapMaterial',
      uniforms,
      vertexShader: shader.vertexShader,
      fragmentShader: shader.fragmentShader,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });
  }, [cubeTexture]);

  useEffect(() => () => material?.dispose(), [material]);

  if (!material) return null;
  return (
    <mesh position={shell.position} material={material} frustumCulled={false} renderOrder={-1000}>
      <boxGeometry args={shell.size} />
    </mesh>
  );
}

function useProjectedEnvironmentMaterial(
  environment: VideoEnvironmentDefinition,
  portraitFraming: boolean,
  cubemapActive: boolean,
) {
  const sourceTexture = useLoader(THREE.TextureLoader, environment.imageSrc);
  const { gl } = useThree();

  const texture = useMemo(() => {
    const next = sourceTexture.clone();
    next.colorSpace = THREE.SRGBColorSpace;
    next.wrapS = THREE.ClampToEdgeWrapping;
    next.wrapT = THREE.ClampToEdgeWrapping;
    next.anisotropy = Math.min(8, gl.capabilities.getMaxAnisotropy());
    next.needsUpdate = true;
    return next;
  }, [gl, sourceTexture]);

  useEffect(() => {
    setState({ backgroundReady: true });
    return () => texture.dispose();
  }, [texture]);

  const material = useMemo(() => {
    // The authored plate is 16:9. Keep its projection calibrated to that
    // source in portrait too; the real camera handles the safe vertical crop.
    // Using the phone aspect here squeezes the background geometry and makes
    // grounded blocks look much larger than their depth warrants.
    const projector = new THREE.PerspectiveCamera(
      environment.camera.fov,
      environment.camera.aspect,
      0.08,
      160,
    );
    projector.position.set(...environment.camera.position);
    projector.lookAt(...environment.camera.target);
    projector.updateMatrixWorld(true);
    projector.updateProjectionMatrix();

    const projectorMatrix = new THREE.Matrix4().multiplyMatrices(
      projector.projectionMatrix,
      projector.matrixWorldInverse,
    );

    return new THREE.ShaderMaterial({
      uniforms: {
        map: { value: texture },
        projectorMatrix: { value: projectorMatrix },
        fallbackColor: { value: new THREE.Color('#17130f') },
        transparentOutside: { value: portraitFraming || cubemapActive },
      },
      vertexShader: environmentVertexShader,
      fragmentShader: environmentFragmentShader,
      toneMapped: false,
      transparent: portraitFraming || cubemapActive,
      depthWrite: !(portraitFraming || cubemapActive),
    });
  }, [cubemapActive, environment, portraitFraming, texture]);

  useEffect(() => () => material.dispose(), [material]);
  return material;
}

function usePortraitCoverageMaterial(environment: VideoEnvironmentDefinition) {
  const sourceTexture = useLoader(THREE.TextureLoader, environment.imageSrc);

  const material = useMemo(() => new THREE.MeshBasicMaterial({
    map: sourceTexture,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  }), [sourceTexture]);

  useEffect(() => () => material.dispose(), [material]);
  return material;
}

function EnvironmentArchitecture({ environment, portraitFraming, cubemapActive }: { environment: VideoEnvironmentDefinition; portraitFraming: boolean; cubemapActive: boolean }) {
  const environmentMaterial = useProjectedEnvironmentMaterial(environment, portraitFraming, cubemapActive);
  const portraitCoverageMaterial = usePortraitCoverageMaterial(environment);
  const { camera, size } = useThree();
  const floor = useMemo(() => resolveWalkableFloor(environment), [environment]);
  const extents = useMemo(() => floorExtents(floor), [floor]);
  const centerZ = (extents.minZ + extents.maxZ) / 2;
  const portraitCoverage = environment.id !== 'clubroom' && size.width < size.height ? 24 : 1.2;
  const depth = extents.depth + portraitCoverage;
  const width = extents.width + portraitCoverage;
  const centerX = (extents.minX + extents.maxX) / 2;
  const portraitCoveragePlane = useMemo(() => {
    const distance = 50;
    const fov = camera instanceof THREE.PerspectiveCamera ? camera.fov : environment.camera.fov;
    const height = 2 * Math.tan(THREE.MathUtils.degToRad(fov) / 2) * distance;
    const aspect = size.width / Math.max(size.height, 1);
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    return {
      height,
      width: height * aspect,
      position: camera.position.clone().addScaledVector(direction, distance),
      quaternion: camera.quaternion.clone(),
    };
  }, [camera, environment.camera.fov, size.height, size.width]);

  return (
    <group>
      {!cubemapActive && (
        <mesh position={[centerX, 0, centerZ]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow material={environmentMaterial}>
          <planeGeometry args={[width, depth]} />
        </mesh>
      )}

      {!cubemapActive && (
        <>
          {[extents.minX - 0.18, extents.maxX + 0.18].map((x) => (
            <mesh key={x} position={[x, environment.shellHeight / 2, centerZ]} receiveShadow material={environmentMaterial}>
              <boxGeometry args={[0.36, environment.shellHeight, depth]} />
            </mesh>
          ))}
          {environment.ceiling && (
            <mesh position={[centerX, environment.shellHeight, centerZ]} receiveShadow material={environmentMaterial}>
              <boxGeometry args={[width, 0.26, depth]} />
            </mesh>
          )}
          <mesh position={[centerX, environment.shellHeight / 2, extents.minZ - 0.18]} receiveShadow material={environmentMaterial}>
            <boxGeometry args={[width, environment.shellHeight, 0.36]} />
          </mesh>
          <mesh position={[centerX, 45, extents.minZ - 0.42]} material={environmentMaterial}>
            <planeGeometry args={[140, 90]} />
          </mesh>
        </>
      )}
      {!cubemapActive && environment.id !== 'clubroom' && size.width < size.height && (
        <mesh
          position={portraitCoveragePlane.position}
          quaternion={portraitCoveragePlane.quaternion}
          material={portraitCoverageMaterial}
          renderOrder={-20}
          frustumCulled={false}
        >
          <planeGeometry args={[portraitCoveragePlane.width, portraitCoveragePlane.height]} />
        </mesh>
      )}

    </group>
  );
}

function Scene({
  activeNpcId,
  activeDialogueChoiceId,
  controlsEnabled,
  environment,
  npcs,
  onNpcEnded,
  onHudChange,
  onNpcPositionResolved,
  onVideoReady,
  onSpriteViewChange,
  reactionKey,
  reactingNpcId,
  renderMode,
  resetKey,
  portraitFraming,
}: {
  activeNpcId: NpcId | null;
  activeDialogueChoiceId: string | null;
  controlsEnabled: boolean;
  environment: VideoEnvironmentDefinition;
  npcs: ResolvedNpcDefinition[];
  onNpcEnded: (id: NpcId) => void;
  onHudChange: (state: HudState) => void;
  onNpcPositionResolved: (id: NpcId, position: WorldPoint) => void;
  onVideoReady: (id: NpcId) => void;
  onSpriteViewChange: (view: string, relativeYawDegrees: number) => void;
  reactionKey: number;
  reactingNpcId: NpcId | null;
  renderMode: ActorRenderMode;
  resetKey: string;
  portraitFraming: boolean;
}) {
  const { gl } = useThree();
  const [cubemapState, setCubemapState] = useState<{ environmentId: EnvironmentId | null; ready: boolean }>({
    environmentId: null,
    ready: false,
  });
  const cubemapReady = cubemapState.environmentId === environment.id && cubemapState.ready;
  const handleCubemapReady = useCallback((ready: boolean) => {
    setCubemapState({ environmentId: environment.id, ready });
  }, [environment.id]);

  useEffect(() => {
    gl.setClearColor('#000000', portraitFraming ? 0 : 1);
    gl.setClearAlpha(portraitFraming ? 0 : 1);
  }, [gl, portraitFraming]);

  return (
    <>
      <CubemapBackdrop key={environment.id} environment={environment} onReady={handleCubemapReady} />
      <fog attach="fog" args={['#14110d', 18, 42]} />
      <ambientLight intensity={0.56} color="#e2c998" />
      <directionalLight position={[-3, 7, 5]} intensity={1.25} color="#fff0c6" castShadow />
      <Suspense fallback={null}>
        <EnvironmentArchitecture key={environment.id} environment={environment} portraitFraming={portraitFraming} cubemapActive={cubemapReady} />
      </Suspense>
      {npcs.map((npc) => (
        <group key={npc.id}>
          {renderMode === 'sprite' && SPRITE_NPC_IDS.has(npc.id) ? (
            <SpriteNpc
              npc={npc}
              onPositionResolved={onNpcPositionResolved}
              onVideoReady={onVideoReady}
              onSpriteViewChange={onSpriteViewChange}
              reportView={npc.id === 'zoe'}
              forceFront={activeNpcId === npc.id}
              dialogueReaction={activeNpcId === npc.id && activeDialogueChoiceId
                ? NPC_DIALOGUE_REACTIONS[npc.id][activeDialogueChoiceId] ?? null
                : null}
            />
          ) : renderMode === 'frames' ? (
            <VideoNpc
              active={reactingNpcId === npc.id}
              npc={npc}
              onEnded={onNpcEnded}
              onPositionResolved={onNpcPositionResolved}
              onVideoReady={onVideoReady}
              replayKey={reactingNpcId === npc.id ? reactionKey : 0}
            />
          ) : (
            <BlockNpc
              npc={npc}
              onPositionResolved={onNpcPositionResolved}
              onVideoReady={onVideoReady}
            />
          )}
        </group>
      ))}
      <FirstPersonController
        activeConversationNpc={activeNpcId ? npcs.find((npc) => npc.id === activeNpcId) ?? null : null}
        controlsEnabled={controlsEnabled}
        environment={environment}
        npcs={npcs}
        onHudChange={onHudChange}
        resetKey={resetKey}
      />
    </>
  );
}

function clampReputation(next: ReputationState): ReputationState {
  return {
    respect: THREE.MathUtils.clamp(next.respect, 0, 10),
    trouble: THREE.MathUtils.clamp(next.trouble, 0, 10),
    rumor: THREE.MathUtils.clamp(next.rumor, 0, 10),
  };
}

function EnvironmentPicker({
  environmentId,
  onSelect,
  testIdPrefix = '',
}: {
  environmentId: EnvironmentId;
  onSelect: (id: EnvironmentId) => void;
  testIdPrefix?: string;
}) {
  return (
    <fieldset className="school-world__location-picker">
      <legend>Choose your route</legend>
      <div>
        {ENVIRONMENTS.map((option) => (
          <button
            key={option.id}
            className={environmentId === option.id ? 'is-selected' : ''}
            data-testid={`${testIdPrefix}background-option-${option.id}`}
            type="button"
            aria-pressed={environmentId === option.id}
            onClick={() => onSelect(option.id)}
          >
            <span style={{ backgroundImage: `url(${option.imageSrc})` }} />
            <strong>{option.name}</strong>
            <small>{option.description}</small>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export default function VideoBillboardGame({ onBack }: VideoBillboardGameProps) {
  const viewport = useViewportSize();
  const [environmentId, setEnvironmentId] = useState<EnvironmentId>('clubroom');
  const [blockingScenario, setBlockingScenario] = useState<ActiveBlockingScenario>('baseline');
  const actorRenderMode: ActorRenderMode = 'sprite';
  const [entered, setEntered] = useState(false);
  const [backgroundPickerOpen, setBackgroundPickerOpen] = useState(false);
  const [renderedNpcPositions, setRenderedNpcPositions] = useState<Partial<Record<NpcId, WorldPoint>>>({});
  const [readyIds, setReadyIds] = useState<Set<NpcId>>(() => new Set());
  const [hud, setHud] = useState<HudState>({ closestNpcId: null, distance: 11, canInteract: false });
  const [activeNpcId, setActiveNpcId] = useState<NpcId | null>(null);
  const [selectedChoice, setSelectedChoice] = useState<DialogueChoice | null>(null);
  const [reputation, setReputation] = useState<ReputationState>(INITIAL_REPUTATION);
  const [reactingNpcId, setReactingNpcId] = useState<NpcId | null>(null);
  const [reactionKey, setReactionKey] = useState(0);
  const [zoeSpriteDebug, setZoeSpriteDebug] = useState({ view: 'front', relativeYawDegrees: 0 });
  const environment = ENVIRONMENTS.find((item) => item.id === environmentId) ?? ENVIRONMENTS[0];
  const mobileFraming = viewport.height > viewport.width;
  const solverViewport = mobileFraming ? viewport : undefined;
  const layoutCheck = useMemo(() => validateBackgroundLayout(environment), [environment]);
  const scenarioPlacements = useMemo(() => {
    if (environment.id === 'clubroom' || blockingScenario === 'baseline') {
      return new Map<string, BlockingPlacement>();
    }
    return new Map(
      resolveBlockingScenario(environment, blockingScenario, environment.id === 'heritage' ? 31 : 47, solverViewport)
        .map((placement) => [placement.id, placement]),
    );
  }, [blockingScenario, environment, solverViewport]);
  const floorBounds = useMemo(
    () => floorExtents(resolveWalkableFloor(environment)),
    [environment],
  );
  const npcs = useMemo(() => {
    const placementById = new Map(layoutCheck.placements.map((placement) => [placement.id, placement]));
    return NPCS.map((npc) => {
      const placement = placementById.get(npc.id);
      const pose = resolveNpcPose(npc, environment, placement, scenarioPlacements.get(npc.id), solverViewport);
      return {
        ...npc,
        feet: pose.feet,
        facingY: pose.facingY,
        blockingRole: pose.note,
        action: pose.action,
        movementTarget: pose.movementTarget,
        pathWorld: pose.pathWorld,
        position: pose.worldPosition,
        scale: resolveBillboardGeometry(
          npc.heightMeters,
          NPC_BILLBOARD_ASSETS[npc.id].views[0],
          SOURCE_PLANE_HEIGHT,
        ).scale * (placement?.scaleCorrection ?? npc.defaults.scaleCorrection),
      };
    });
  }, [environment, layoutCheck.placements, scenarioPlacements, solverViewport]);
  const playerMovementBounds = useMemo(() => movementBoundsAroundPoints(
    [environment.camera.position, ...npcs.map((npc) => npc.position)],
    environment.movementMargin,
  ), [environment.camera.position, environment.movementMargin, npcs]);
  const placementState = useMemo(() => {
    const npcById = new Map(npcs.map((npc) => [npc.id, npc]));
    return NPCS.map((sourceNpc) => {
      const npc = npcById.get(sourceNpc.id);
      if (!npc) throw new Error(`Unknown NPC anchor ${sourceNpc.id}`);
      return {
        id: npc.id,
        feet: npc.feet,
        worldPosition: npc.position,
        facingY: npc.facingY,
        characterHeightMeters: npc.heightMeters,
        resolvedScale: npc.scale,
        projectedHeightPx: projectedHeightPixels(
          npc.position,
          SOURCE_PLANE_HEIGHT * npc.scale,
          environment.camera,
          viewport.height,
        ),
        projectedBounds: projectedActorBounds(
          npc.position,
          SOURCE_PLANE_HEIGHT * npc.scale,
          SOURCE_PLANE_HEIGHT * npc.scale
            * (NPC_BILLBOARD_ASSETS[npc.id].views[0].canvasWidth
              / NPC_BILLBOARD_ASSETS[npc.id].views[0].canvasHeight),
          environment.camera,
          viewport,
        ),
        screenVisible: false,
        insideWalkableFloor: pointInPolygonXZ(npc.position, resolveWalkableFloor(environment)),
        role: npc.blockingRole,
        action: npc.action,
        movementTarget: npc.movementTarget,
        path: npc.pathWorld,
      };
    });
  }, [environment, npcs, viewport]);
  const screenFrame = useMemo(() => {
    const safeMargin = mobileFraming ? 18 : 12;
    const visible = placementState.every((placement) => {
      const bounds = placement.projectedBounds;
      return bounds.inFront
        && bounds.left >= safeMargin
        && bounds.top >= safeMargin
        && bounds.right <= viewport.width - safeMargin
        && bounds.bottom <= viewport.height - safeMargin
        && bounds.heightPx >= (mobileFraming ? 32 : 20);
    });
    const separation = placementState.reduce((minimum, placement, index) => {
      for (const other of placementState.slice(index + 1)) {
        minimum = Math.min(minimum, screenSpaceSeparationPx(placement.projectedBounds, other.projectedBounds));
      }
      return minimum;
    }, Number.POSITIVE_INFINITY);
    return { visible, separation };
  }, [mobileFraming, placementState, viewport]);
  const visiblePlacementState = useMemo(() => placementState.map((placement) => ({
    ...placement,
    screenVisible: placement.projectedBounds.inFront
      && placement.projectedBounds.left >= (mobileFraming ? 18 : 12)
      && placement.projectedBounds.top >= (mobileFraming ? 18 : 12)
      && placement.projectedBounds.right <= viewport.width - (mobileFraming ? 18 : 12)
      && placement.projectedBounds.bottom <= viewport.height - (mobileFraming ? 18 : 12)
      && placement.projectedBounds.heightPx >= (mobileFraming ? 32 : 20),
  })), [mobileFraming, placementState, viewport]);
  const dialogueOpen = activeNpcId !== null;
  const activeNpc = activeNpcId ? NPC_BY_ID.get(activeNpcId) ?? null : null;
  const closestNpc = hud.closestNpcId ? NPC_BY_ID.get(hud.closestNpcId) ?? null : null;
  const readyCount = readyIds.size;
  const videosReady = readyCount >= NPCS.length;
  const controlsEnabled = entered && !dialogueOpen && !backgroundPickerOpen;

  const chooseDialogue = useCallback((choice: DialogueChoice) => {
    if (!activeNpcId) return;
    const next = clampReputation({
      respect: reputation.respect + choice.respectDelta,
      trouble: reputation.trouble + choice.troubleDelta,
      rumor: reputation.rumor + choice.rumorDelta,
    });
    setSelectedChoice(choice);
    setReputation(next);
    setReactingNpcId(activeNpcId);
    setReactionKey((value) => value + 1);
    setState({
      currentAction: `${activeNpcId}:react`,
      selectedChoiceLabel: choice.label,
      respect: next.respect,
      trouble: next.trouble,
      rumor: next.rumor,
    });
  }, [activeNpcId, reputation]);

  const openDialogue = useCallback((id: NpcId) => {
    const npc = NPC_BY_ID.get(id);
    if (!npc) return;
    setActiveNpcId(id);
    setSelectedChoice(null);
    setState({
      activeNpcId: id,
      dialogueOpen: true,
      selectedChoiceLabel: '',
      currentAction: `${id}:idle`,
    });
  }, []);

  const closeDialogue = useCallback(() => {
    setActiveNpcId(null);
    setSelectedChoice(null);
    setState({ activeNpcId: null, dialogueOpen: false });
  }, []);

  const handleHudChange = useCallback((next: HudState) => {
    setHud(next);
  }, []);

  const handleVideoReady = useCallback((id: NpcId) => {
    setReadyIds((current) => {
      if (current.has(id)) return current;
      const next = new Set(current);
      next.add(id);
      setState({ videoReady: next.size >= NPCS.length });
      return next;
    });
  }, []);

  const handleNpcEnded = useCallback((id: NpcId) => {
    if (reactingNpcId !== id) return;
    setReactingNpcId(null);
    setState({ currentAction: activeNpcId ? `${activeNpcId}:idle` : 'idle' });
  }, [activeNpcId, reactingNpcId]);

  const handleNpcPositionResolved = useCallback((id: NpcId, position: WorldPoint) => {
    setRenderedNpcPositions((current) => {
      const existing = current[id];
      if (existing && existing.every((value, index) => value === position[index])) return current;
      return { ...current, [id]: position };
    });
  }, []);

  useEffect(() => {
    window.__videoBillboardState = {
      ready: true,
      videoReady: false,
      characterVisible: false,
      characterPosition: npcs[0].position,
      cameraPosition: environment.camera.position,
      distanceToCharacter: 11,
      interactionAvailable: false,
      pointerLocked: false,
      mouseDragging: false,
      mouseLookReady: true,
      cameraRotation: [0, 0],
      viewport,
      mobileFraming,
      currentAction: 'idle',
      chromaKey: false,
      hallwayReady: true,
      npcCount: NPCS.length,
      closestNpcId: null,
      activeNpcId: null,
      dialogueOpen: false,
      selectedChoiceLabel: '',
      respect: INITIAL_REPUTATION.respect,
      trouble: INITIAL_REPUTATION.trouble,
      rumor: INITIAL_REPUTATION.rumor,
      environmentId: environment.id,
      environmentName: environment.name,
      backgroundReady: window.__videoBillboardState?.backgroundReady ?? false,
      backgroundImageSrc: environment.imageSrc,
      backgroundMode: environment.backdrop,
      floorDepthMeters: floorBounds.depth,
      movementBounds: {
        ...playerMovementBounds,
      },
      npcPlacements: visiblePlacementState,
      spaceClass: environment.analysis.spaceClass,
      walkableFloorValid: layoutCheck.valid,
      renderedNpcPositions: {},
      actorRenderMode,
      spriteNpcCount: actorRenderMode === 'sprite' ? SPRITE_NPC_IDS.size : 0,
      zoeSpriteView: null,
      zoeSpriteYawDegrees: 0,
      blockingScenario,
      blockingScenarioLabel: environment.id === 'clubroom'
        ? 'Approved Debate Club Room baseline'
        : BLOCKING_SCENARIOS.find((scenario) => scenario.id === blockingScenario)?.label ?? '',
      minScreenSeparationPx: screenFrame.separation,
      screenFrameValid: screenFrame.visible && screenFrame.separation >= (mobileFraming ? PORTRAIT_MIN_SCREEN_GAP_PX : 0),
    };
  }, [actorRenderMode, blockingScenario, environment, floorBounds, layoutCheck.valid, mobileFraming, npcs, playerMovementBounds, screenFrame, viewport, visiblePlacementState]);

  useEffect(() => {
    setState({
      dialogueOpen,
      activeNpcId,
      respect: reputation.respect,
      trouble: reputation.trouble,
      rumor: reputation.rumor,
      videoReady: videosReady,
      chromaKey: false,
      environmentId: environment.id,
      environmentName: environment.name,
      backgroundImageSrc: environment.imageSrc,
      backgroundMode: environment.backdrop,
      floorDepthMeters: floorBounds.depth,
      movementBounds: {
        ...playerMovementBounds,
      },
      npcPlacements: visiblePlacementState,
      spaceClass: environment.analysis.spaceClass,
      walkableFloorValid: layoutCheck.valid,
      renderedNpcPositions,
      actorRenderMode,
      spriteNpcCount: actorRenderMode === 'sprite' ? SPRITE_NPC_IDS.size : 0,
      zoeSpriteView: actorRenderMode === 'sprite' ? zoeSpriteDebug.view : null,
      zoeSpriteYawDegrees: actorRenderMode === 'sprite' ? zoeSpriteDebug.relativeYawDegrees : 0,
      blockingScenario,
      blockingScenarioLabel: environment.id === 'clubroom'
        ? 'Approved Debate Club Room baseline'
        : BLOCKING_SCENARIOS.find((scenario) => scenario.id === blockingScenario)?.label ?? '',
      viewport,
      mobileFraming,
      minScreenSeparationPx: screenFrame.separation,
      screenFrameValid: screenFrame.visible && screenFrame.separation >= (mobileFraming ? PORTRAIT_MIN_SCREEN_GAP_PX : 0),
    });
  }, [
    activeNpcId,
    dialogueOpen,
    environment,
    floorBounds,
    layoutCheck.valid,
    visiblePlacementState,
    renderedNpcPositions,
    reputation,
    actorRenderMode,
    zoeSpriteDebug,
    blockingScenario,
    videosReady,
    mobileFraming,
    playerMovementBounds,
    screenFrame,
    viewport,
  ]);

  useEffect(() => {
    if (environment.id === 'clubroom') return undefined;
    setState({ backgroundReady: false });
    let frames = 0;
    let frameHandle = 0;
    const markReadyAfterRender = () => {
      frames += 1;
      if (frames < 4) {
        frameHandle = window.requestAnimationFrame(markReadyAfterRender);
        return;
      }
      setState({ backgroundReady: true });
    };
    frameHandle = window.requestAnimationFrame(markReadyAfterRender);
    return () => window.cancelAnimationFrame(frameHandle);
  }, [blockingScenario, environment.id]);

  const chooseEnvironment = useCallback((nextId: EnvironmentId) => {
    setBackgroundPickerOpen(false);
    if (nextId === environmentId) return;
    setEnvironmentId(nextId);
    setBlockingScenario('baseline');
    setHud({ closestNpcId: null, distance: 11, canInteract: false });
    setActiveNpcId(null);
    setSelectedChoice(null);
    setReactingNpcId(null);
    setRenderedNpcPositions({});
    setState({
      activeNpcId: null,
      backgroundReady: false,
      closestNpcId: null,
      dialogueOpen: false,
      interactionAvailable: false,
    });
  }, [environmentId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape' && backgroundPickerOpen) {
        setBackgroundPickerOpen(false);
        return;
      }
      if (event.code === 'Escape' && dialogueOpen) {
        closeDialogue();
        return;
      }
      if (!dialogueOpen && event.code === 'KeyE') {
        const targetNpcId = hud.closestNpcId ?? window.__videoBillboardState?.closestNpcId ?? null;
        if (targetNpcId) openDialogue(targetNpcId);
        return;
      }
      if (!dialogueOpen || !activeNpc) return;
      const indexByKey: Record<string, number> = { Digit1: 0, Digit2: 1, Digit3: 2 };
      const index = indexByKey[event.code];
      if (index === undefined) return;
      const choice = activeNpc.choices[index];
      if (choice) chooseDialogue(choice);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeNpc, backgroundPickerOpen, chooseDialogue, closeDialogue, dialogueOpen, hud.closestNpcId, openDialogue]);

  return (
    <main className="video-world school-world" data-testid="video-billboard-game">
      <Canvas
        className={mobileFraming ? 'video-world__portrait-canvas' : undefined}
        style={mobileFraming ? { '--portrait-background': `url(${environment.imageSrc})` } as CSSProperties : undefined}
        camera={{ fov: environment.camera.fov, near: 0.08, far: 160, position: environment.camera.position }}
        dpr={[1, 1.65]}
        gl={{ antialias: true, alpha: true }}
        shadows="basic"
      >
        <Scene
          activeNpcId={activeNpcId}
          activeDialogueChoiceId={selectedChoice?.id ?? null}
          controlsEnabled={controlsEnabled}
          environment={environment}
          npcs={npcs}
          onNpcEnded={handleNpcEnded}
          onHudChange={handleHudChange}
          onNpcPositionResolved={handleNpcPositionResolved}
          onVideoReady={handleVideoReady}
          onSpriteViewChange={(view, relativeYawDegrees) => setZoeSpriteDebug({ view, relativeYawDegrees })}
          reactionKey={reactionKey}
          reactingNpcId={reactingNpcId}
          renderMode={actorRenderMode}
          resetKey={`${environment.id}:${blockingScenario}:${mobileFraming ? 'portrait' : 'desktop'}:${viewport.width}x${viewport.height}`}
          portraitFraming={mobileFraming}
        />
      </Canvas>

      <div className="video-world__vignette" aria-hidden="true" />
      <div className="video-world__grain" aria-hidden="true" />
      <div className="video-world__reticle" aria-hidden="true"><span /></div>

      <header className="video-world__header">
        <div className="video-world__nav-actions">
          <button className="video-world__back" type="button" onClick={onBack}>Exit school</button>
          {entered && (
            <button
                className="video-world__switch"
                data-testid="switch-background"
                type="button"
                onClick={() => setBackgroundPickerOpen(true)}
              >
                Switch background
              </button>
          )}
        </div>
        <div>
          <p>AFTER FIRST BELL · {environment.name} · {SPACE_CLASS_LABEL[environment.analysis.spaceClass]}</p>
          <h1>Academy Hallway RPG</h1>
        </div>
        <span className={`video-world__status ${videosReady ? 'is-ready' : ''}`} data-testid="video-status">
          {videosReady && layoutCheck.valid ? 'CAST READY' : `LOADING CAST ${readyCount}/${NPCS.length}`}
        </span>
      </header>

      <aside className="school-world__stats" aria-label="RPG state">
        <div><span>RESPECT</span><strong data-testid="respect-meter">{reputation.respect}</strong></div>
        <div><span>TROUBLE</span><strong data-testid="trouble-meter">{reputation.trouble}</strong></div>
        <div><span>RUMOR</span><strong data-testid="rumor-meter">{reputation.rumor}</strong></div>
      </aside>

      <aside className="school-world__roster" aria-label="Nearby cast">
        {npcs.map((npc) => (
          <div
            key={npc.id}
            className={[
              'school-world__roster-row',
              hud.closestNpcId === npc.id ? 'is-near' : '',
              activeNpcId === npc.id ? 'is-active' : '',
            ].join(' ')}
          >
            <i style={{ backgroundColor: npc.accent }} />
            <span>{npc.name}</span>
            <strong>{npc.clique}</strong>
          </div>
        ))}
      </aside>

      <div className={`video-world__prompt ${hud.canInteract && !dialogueOpen ? 'is-visible' : ''}`}>
        <kbd>E</kbd><span>{closestNpc ? `Talk to ${closestNpc.name}` : 'Talk'}</span>
      </div>

      {entered && !dialogueOpen && !backgroundPickerOpen && (
        <div className="school-world__control-hint" data-testid="mouse-control-hint">
          <span>DRAG MOUSE</span> LOOK <i />
          <span>WASD</span> MOVE <i /> <span>E</span> TALK
        </div>
      )}

      {dialogueOpen && activeNpc && (
        <section className="school-world__dialogue" data-testid="dialogue-panel">
          <div className="school-world__speaker">
            <i style={{ backgroundColor: activeNpc.accent }} />
            <div>
              <span>{activeNpc.role}</span>
              <h2>{activeNpc.name}</h2>
            </div>
            <button type="button" onClick={closeDialogue} aria-label="Close dialogue">X</button>
          </div>
          <p className="school-world__bark" data-testid="npc-bark">
            {selectedChoice ? selectedChoice.response : activeNpc.bark}
          </p>
          {selectedChoice && (
            <p className="school-world__player-line" data-testid="player-line">
              You: {selectedChoice.playerLine}
            </p>
          )}
          <div className="school-world__choices">
            {activeNpc.choices.map((choice, index) => (
              <button
                key={choice.id}
                type="button"
                data-testid={`dialogue-choice-${index + 1}`}
                onClick={() => chooseDialogue(choice)}
              >
                <span>{index + 1}</span>
                {choice.label}
              </button>
            ))}
          </div>
        </section>
      )}

      {!entered && (
        <section className="video-world__entry" data-testid="entry-overlay">
          <div className="video-world__entry-card">
            <p className="video-world__eyebrow">OPEN HALL PROTOTYPE</p>
            <h2>Survive the passing period.</h2>
            <p>
              A grimy academy hallway with generated video characters, clique pressure,
              and quick-choice RPG conversations.
            </p>
            <EnvironmentPicker environmentId={environmentId} onSelect={chooseEnvironment} />
            <button type="button" onClick={() => setEntered(true)}>
              Start school day
            </button>
          </div>
        </section>
      )}

      {entered && backgroundPickerOpen && (
        <section className="school-world__background-switcher" data-testid="background-switcher">
          <div className="school-world__background-switcher-card">
            <header>
              <div>
                <p className="video-world__eyebrow">CHANGE LOCATION</p>
                <h2>Switch school space</h2>
              </div>
              <button
                type="button"
                aria-label="Close background switcher"
                onClick={() => setBackgroundPickerOpen(false)}
              >
                ×
              </button>
            </header>
            <EnvironmentPicker
              environmentId={environmentId}
              onSelect={chooseEnvironment}
              testIdPrefix="game-"
            />
          </div>
        </section>
      )}
    </main>
  );
}
